import { createSignal } from "solid-js";
import { render } from "solid-js/web";
import { createComponent } from "solid-js";
import type { Header } from "../replay/format";
import { SAT_LABELS, type Mark, type Sat } from "../render/colour";
import type { RenderOptions } from "../render/renderer";
import { LayersTab, TOGGLES, ViewTab } from "../console/ViewTabs";
import { $ } from "./format";

export { TOGGLES };

/** The console's View and Layers tabs (Solid components, `console/ViewTabs`)
 *  over the renderer's options, and the changes keys make to them. */
export class Panel {
  private bump: () => void;
  private disposers: (() => void)[] = [];

  constructor(
    header: Header,
    private options: RenderOptions,
    private changed: () => void,
  ) {
    const [rev, setRev] = createSignal(0);
    this.bump = () => setRev((v) => v + 1);
    const props = {
      header,
      options,
      rev,
      change: (f: (o: RenderOptions) => void) => {
        f(options);
        this.bump();
        changed();
      },
    };
    this.disposers.push(render(() => createComponent(ViewTab, props), $("view-body")));
    this.disposers.push(render(() => createComponent(LayersTab, props), $("layers-body")));
  }

  setMark(m: Mark): void {
    this.options.mark = m;
    this.sync();
    this.changed();
  }

  setSat(v: Sat): void {
    this.options.sat = v;
    this.sync();
    this.changed();
  }

  /** Next saturation measure (Q). */
  cycleSat(): string {
    const i = SAT_LABELS.findIndex(([v]) => v === this.options.sat);
    const [v, label] = SAT_LABELS[(i + 1) % SAT_LABELS.length];
    this.setSat(v);
    return label;
  }

  toggle(key: keyof RenderOptions): void {
    (this.options[key] as boolean) = !this.options[key];
    this.sync();
    this.changed();
  }

  /** The tabs re-read the options (after a change made elsewhere). */
  sync(): void {
    this.bump();
  }

  dispose(): void {
    this.disposers.forEach((d) => d());
  }
}
